import './CreateEditNewSignDocModal.scss'
import {Modal} from "../../../Smart/Modal/Modal";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {FormInput} from "../../../Form/Input/FormInput";
import {Button} from "../../../Form/Button/Button";
import {buttonColors} from "../../../Pickers/InputTheme";
import {FileUploadBox} from "../../../Smart/FileUploadBox/FileUploadBox";
import {MultiSelectAutocomplete} from "../../../Form/AutoComplete/AutoComplete";
import {onProgress} from "../../../Smart/FileUploadBox/FileUploadBoxLib";
import {
    createSignDocAPI,
    updateSignDocAPI,
    deleteSignDocAPI,
    uploadDocumentFileAPI,
    updateSignDocUploadStatusAPI,
    getApproverUsersWithSelectedAPI,
    getSignDocVersionsAPI,
    lookupUsersByPhonesAPI,
    getUsersByIdsAPI,
    checkNotificationQuotaAPI,
} from "../../../../Services/api";
import {get, map, isEmpty, keys, uniq, filter} from 'lodash'
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import {useUserAdminOrgs} from "../../../Hooks/UseUserAdminOrgs";
import {useAdminDataPagination} from "../../../Hooks/useAdminDataPagination";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
    isCanvaLink,
    isVimeoLink,
    isYoutubeLink,
    SignDocTypes,
    SignDocUploadStatus,
    SignDocsAssingOrganizationInUpdate,
    DEFAULT_DURATION_DAYS, NO_DURATION,
    DurationUnits,
    VALID_DURATION_UNITS,
    RECURRING_DURATION_UNITS,
    DEFAULT_RECURRING_DURATION,
    MIN_RECURRING_DURATION_VALUES,
    SIGNDOC_GROUP_TYPES,
    DEFAULT_GROUP_TYPE,
    SIGNDOC_STATUS,
    DEFAULT_SIGNDOC_STATUS,
    SIGNDOC_ASSIGNMENT_PAGINATION,
    RECURRING_SIGNDOC_ALLOW_PAST_DATE
} from "../../../SignDoc/SignDocLib";
import {SelectBox} from "../../../Pickers/SelectBox/SelectBox";
import {DurationPicker} from "../../../Pickers/DurationPicker/DurationPicker";
import {DatePicker} from "../../../Pickers/DatePicker/DatePicker";
import {FeatureFlags, SignDocCategories, ExpiryDurationUnits, SignDocErrorCodes} from "../../../../Utils/Constants";
import {ConfirmationModal} from "../../../Smart/confirmationModal/confirmationModal";
import {FormControlLabel, Switch, Alert, Tooltip} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {LocalTabs} from "../../../../Utils/RoutingTabs/Tabs";
import {CompanyReactContext} from "../../../../contexts/CompanyContext";
import {SignDocListRefresherContext} from "../../../../contexts/SignDocListRefresherContext";
import {
    MessageTypeSelectionButtons
} from "../../../Notifications/MessageTypeSelectionButtons/MessageTypeSelectionButtons";
import {questionnaireTypes} from "../../../Questionnaires/QuestionnaireConstants";
import useSignDocTypes from '../../../../Hooks/useSignDocTypes';
import {createSubtype, hasSubtype, updateSubtype} from "../../../SignDoc/SignDocSubtypeHandler";
import {OrganizationAssignmentWarningModal} from "./OrganizationAssignmentWarningModal/OrganizationAssignmentWarningModal";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {useNavigateAfterSignDocSave} from "../../../../Hooks/useNavigateAfterSignDocSave";
import {DateTimePicker} from "@mui/x-date-pickers/DateTimePicker";
import {invalidateQueries, queryKeys} from "../../../Hooks/useQueryHooks";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFnsV3";
import {he} from "date-fns/locale";
import { SignDocTypesThatSupportApprovals, MEETING_PLATFORMS, matchesPlatformPattern } from "../../../SignDoc/SignDocLib";
import useDebounce from "../../../../Hooks/useDebounce";
import {ChipsPaginationWrapper} from "../../../ChipsPaginationWrapper/ChipsPaginationWrapper";
import {AlertDialog, AlertTypesEnum} from "../../../Smart/AlertDialog/AlertDialog";
import {UploadButton} from "../../../Form/Button/UploadButton";
import {importUsersFromFile} from "../../../../Utils/excel";
import ImportIcon from '@mui/icons-material/FileUpload';
import {ImportUsersPreviewModal} from "../../../Users/ImportUsersPreview/ImportUsersPreviewModal";
import {formatPhoneForDisplay, formatPhoneToE164} from "../../../../Utils/functions";

/**
 * Modal component for creating and editing SignDoc documents with upload status handling.
 * Supports video file uploads with user messaging during upload process.
 *
 * video links example
 * https://vimeo.com/990606130
 * https://www.youtube.com/watch?v=6rNezfIl5jc&t=51s
 * https://youtu.be/6rNezfIl5jc?si=LAbHg9ETHD4OpGVK
 * https://www.youtube.com/embed/6rNezfIl5jc?si=LAbHg9ETHD4OpGVK
 **/

/**
 * Builds category options based on available feature flags
 * @param {Object} companyContext - Company context with feature flags
 * @param {Array} allowedCategories - Optional array of allowed categories to filter by
 * @param {Object} i18n - i18n translation object
 * @returns {Array} Array of category options for SelectBox
 */
const getCategoryOptions = (companyContext, allowedCategories, i18n) => {
    const allOptions = [
        {
            value: SignDocCategories.FORCE_COMPLETION,
            text: i18n.translate('certifications.categoryForceCompletion')
        }
    ];

    if (companyContext.featureFlags[FeatureFlags.CERTIFICATION]) {
        allOptions.push({
            value: SignDocCategories.CERTIFICATION,
            text: i18n.translate('certifications.categoryCertification')
        });
    }

    if (companyContext.featureFlags[FeatureFlags.KNOWLEDGE_CENTER]) {
        allOptions.push({
            value: SignDocCategories.KNOWLEDGE_CENTER,
            text: i18n.translate('certifications.categoryKnowledgeCenter')
        });
    }

    // If allowedCategories is provided, filter options to only include allowed categories
    if (allowedCategories && allowedCategories.length > 0) {
        return allOptions.filter(option => allowedCategories.includes(option.value));
    }

    return allOptions;
}

/**
 * Modal component for creating and editing SignDoc documents.
 *
 * @param {object} props - Component props
 * @param {boolean} props.newDocSignModalOpen - Controls modal visibility
 * @param {function} props.closeNewDocSignModal - Callback to close modal
 * @param {object} props.signDoc - SignDoc data for editing mode
 * @param {boolean} props.editMode - Whether component is in edit mode
 * @param {function} props.onAfterEditComplete - Callback after edit completes
 * @param {string} [props.signDocType=SignDocTypes.READ_AND_SIGN_DOC_PDF] - Default SignDoc type
 * @param {string[]} [props.SignDocOptionsTypes=[SignDocTypes.READ_AND_SIGN_DOC_PDF]] - Available SignDoc types
 * @param {boolean} props.forKnowledgeCenter - Whether component is used in knowledge center
 * @param {boolean} props.forceCompletion - Whether to force completion
 * @param {boolean} [props.isNewVersion=false] - Whether component is creating a new version
 * @returns {JSX.Element} Modal component for SignDoc creation/editing
 */

export const CreateEditNewSignDocModal = ({
                                              newDocSignModalOpen,
                                              closeNewDocSignModal,
                                              signDoc,
                                              editMode,
                                              onAfterEditComplete,
                                              signDocType = SignDocTypes.READ_AND_SIGN_DOC_PDF,
                                              SignDocOptionsTypes = [SignDocTypes.READ_AND_SIGN_DOC_PDF],
                                              allowedCategories,
                                              withRoute,
                                              isNewVersion = false,
                                              totalVersionCount = 0,
                                          }) => {
    const companyContext = useContext(CompanyReactContext)
    const { refreshSignDocList } = useContext(SignDocListRefresherContext)
    const {i18n} = useContext(I18nReactContext)

    const getInitialCategoryValue = () => {
        if ((editMode || isNewVersion) && signDoc.category) {
            return signDoc.category;
        }
        // If allowedCategories is provided, use the first allowed category
        // Otherwise default to FORCE_COMPLETION
        if (allowedCategories && allowedCategories.length > 0) {
            return allowedCategories[0];
        }
        return SignDocCategories.FORCE_COMPLETION;
    };


    // Fetch ALL organizations (used ONLY for "Assign to All" functionality - lazy loaded)
    const {adminOrganizations: allAdminOrganizations} = useUserAdminOrgs({
        withAllOption: false,
        withCoursesAndSignDocs: false
    });

    // Organization search state (must be declared before hook usage)
    const [orgSearchQuery, setOrgSearchQuery] = useState('') // Search filter for organizations

    // Debounced organization search for hook
    const debouncedOrgSearchForHook = useDebounce({value: orgSearchQuery, delay: 400})

    // Organization pagination hook - pageSize defaults to 30 (from SIGNDOC_ASSIGNMENT_PAGINATION)
    const {
        data: paginatedOrganizations,
        totalCount: orgTotalCount,
        currentPage: orgCurrentPage,
        isLoading: isLoadingOrgs,
        error: orgError,
        setPage: setOrgPage
    } = useAdminDataPagination({
        dataModelType: 'organizations',
        enabled: newDocSignModalOpen,
        searchQuery: debouncedOrgSearchForHook
    });

    const [docName, setDocName] = useState(editMode || isNewVersion ? signDoc.docName : '')
    const [docDescription, setDocDescription] = useState(editMode || isNewVersion ? signDoc.description : '')
    const [progress, setProgress] = useState(0)
    const [files, setFiles] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [selectedOrganizations, setSelectedOrganizations] = useState(new Set())
    const [selectedSignDocType, setSelectedSignDocType] = useState(editMode || isNewVersion ? signDoc.type : signDocType)
    const [linkUrl, setLinkUrl] = useState(editMode ? signDoc.linkUrl : '')
    const [signDocOptions, setSignDocOptions] = useState([])
    const [linkUrlError, setLinkUrlError] = useState('')
    const [notifyOrganizationUserOnNewTask, setNotifyOrganizationUserOnNewTask] = useState(false)
    const [selectedMessagingTypes, setSelectedMessagingTypes] = useState([])
    const [availableOrganizations, setAvailableOrganizations] = useState([])
    const [selectedCategory, setSelectedCategory] = useState(getInitialCategoryValue)
    const [allowFastForward, setAllowFastForward] = useState(editMode || isNewVersion ? !!signDoc.allowFastForward : false)
    // MustDo flag for the external compliance API - only shown and sent when the company has the FF
    const hasMustDoComplianceFF = !!companyContext.featureFlags[FeatureFlags.MUST_DO_COMPLIANCE]
    const [isMustDo, setIsMustDo] = useState(editMode || isNewVersion ? signDoc.isMustDo === true : false)
    // Organization assignment warning modal state
    const [organizationAssignmentWarningModal, setOrganizationAssignmentWarningModal] = useState(false)

    // Enable approvals toggle for UPLOAD_DOCUMENT, FRONTAL_COURSE, and ONLINE_COURSE types
    const [enableApprovals, setEnableApprovals] = useState(
        editMode || isNewVersion ? get(signDoc, 'additionalData.enableApprovals', true) : true
    )

    // Free-path mode flag for LEARNING_PATH type — tasks can be completed in any order.
    // Strict === true so missing/falsy values default to false (ordered mode); `get` already
    // returns the default when `signDoc` is empty (e.g., creating from scratch), so no need
    // to gate on edit/new-version.
    const [unorderedLearningPathTasks, setUnorderedLearningPathTasks] = useState(
        get(signDoc, 'additionalData.unorderedLearningPathTasks', false) === true
    )

    // Confirmation dialog when switching unordered → ordered on an already-assigned LP.
    // Existing per-user progress is preserved, but the next available task becomes the
    // first incomplete one in the sequence — which can feel unexpected. Warn first.
    const [showSwitchToOrderedDialog, setShowSwitchToOrderedDialog] = useState(false);

    /**
     * Intercepts the free-path toggle. Shows the confirmation dialog only when:
     *   currently free-path === true  AND  next value === false  AND  LP is assigned.
     * Reverse direction (ordered → unordered) or unassigned LPs save silently.
     * @param {boolean} nextValue
     */
    const handleFreePathToggle = (nextValue) => {
        const isSwitchingToOrdered = unorderedLearningPathTasks === true && nextValue === false;
        const isAssigned = get(signDoc, 'organizationSignDoc', []).length > 0;
        if (isSwitchingToOrdered && isAssigned) {
            setShowSwitchToOrderedDialog(true);
            return;
        }
        setUnorderedLearningPathTasks(nextValue);
    };

    // Version count state for enabling category change in edit mode
    const [versionCount, setVersionCount] = useState(null)
    const [isLoadingVersionCount, setIsLoadingVersionCount] = useState(false)

    // Approver selection state for UPLOAD_DOCUMENT, FRONTAL_COURSE, and ONLINE_COURSE types
    const [selectedApproverUsers, setSelectedApproverUsers] = useState([])

    // Individual user assignment state - available for all categories
    const [userSearchQuery, setUserSearchQuery] = useState('') // Search filter for users

    // Debounced search query for user API calls (400ms delay)
    const debouncedUserSearch = useDebounce({value: userSearchQuery, delay: 400})

    // Import users from file state
    const [importPreviewData, setImportPreviewData] = useState(null) // { matchedUsers, unmatchedRows } or null
    const [isProcessingImport, setIsProcessingImport] = useState(false)

    // User pagination hook - pageSize defaults to 30 (from SIGNDOC_ASSIGNMENT_PAGINATION)
    const {
        data: availableUsers,
        totalCount: userTotalCount,
        currentPage: userCurrentPage,
        isLoading: isLoadingUsers,
        error: userError,
        setPage: setUserCurrentPage
    } = useAdminDataPagination({
        dataModelType: 'users',
        enabled: true, // Individual assignment available for all categories
        searchQuery: debouncedUserSearch
    });

    // Selected users sub-tab state
    const [selectedUsersDetails, setSelectedUsersDetails] = useState([])
    const [isLoadingSelectedUsers, setIsLoadingSelectedUsers] = useState(false)
    const [selectedUsersPage, setSelectedUsersPage] = useState(0)
    const [selectedUsersSearchQuery, setSelectedUsersSearchQuery] = useState('')

    // Error handling state for pagination
    const [showUserPaginationError, setShowUserPaginationError] = useState(false)
    const [showOrgPaginationError, setShowOrgPaginationError] = useState(false)
    const [showUploadError, setShowUploadError] = useState(false)

    // Course-specific fields state (FRONTAL_COURSE and ONLINE_COURSE)
    const [sessionStartDate, setSessionStartDate] = useState(
        editMode ? get(signDoc, 'additionalData.sessionStartDate', null) : null
    )
    const [courseLocation, setCourseLocation] = useState(
        editMode ? get(signDoc, 'additionalData.location', '') : ''
    )
    const [courseLocationError, setCourseLocationError] = useState(null)

    /**
     * Check if selected type is a course type (FRONTAL or ONLINE)
     * @returns {boolean}
     */
    const isCourseType = () => {
        return [SignDocTypes.FRONTAL_COURSE, SignDocTypes.ONLINE_COURSE].includes(selectedSignDocType);
    };

    /**
     * Check if selected type is specifically FRONTAL_COURSE
     * @returns {boolean}
     */
    const isFrontalCourse = () => {
        return selectedSignDocType === SignDocTypes.FRONTAL_COURSE;
    };

    /**
     * Check if selected type is specifically ONLINE_COURSE
     * @returns {boolean}
     */
    const isOnlineCourse = () => {
        return selectedSignDocType === SignDocTypes.ONLINE_COURSE;
    };

    /**
     * Fetches approver users using single API that includes selected approvers on first page
     * Backend fetches current approvers from DB using signDocId
     * @param {object} params - Parameters object
     * @param {string} params.query - Search query to filter users
     * @param {number} params.page - Page number for pagination
     * @returns {Promise<Array>} Array of user options with selected approvers first on page 0
     */
    const fetchApproverUsers = async ({ query = '', page = 0 }) => {
        try {
            // Backend will fetch current approvers from DB if signDocId is provided
            const response = await getApproverUsersWithSelectedAPI({
                signDocId: editMode ? signDoc.id : null,
                paginationModel: {
                    page,
                    pageSize: SIGNDOC_ASSIGNMENT_PAGINATION.USERS_PAGE_SIZE
                },
                filterModel: {
                    filterValues: {
                        search: query
                    }
                }
            });

            const users = get(response, 'users', []);

            if (!Array.isArray(users)) {
                console.error('Unexpected API response format:', response);
                return [];
            }

            // Map users to autocomplete format
            // Backend already marks selected users with isSelected: true
            const userOptions = users.map(user => ({
                value: user.id,
                text: `${user.firstName} ${user.lastName}`,
                isSelected: user.isSelected
            }));

            return userOptions;
        } catch (error) {
            console.error('Error fetching approver users:', error);
            return [];
        }
    };

    //Questionnaire types
    //TODO this data should not be here, questionnaire specific attributes should be set and handled in the questionnaire component QQQQ
    const [examPassGrade, setExamPassGrade] = useState(get(signDoc, 'questionnaire.passGrade', 0))
    const [isAnonymous, setIsAnonymous] = useState(get(signDoc, 'questionnaire.isAnonymous', false))
    const navigateAfterSignDocSave = useNavigateAfterSignDocSave();

    // Expiry duration state for CERTIFICATION category
    const initialExpiryDuration = editMode && get(signDoc, 'additionalData.expiryDuration')
        ? {
            value: get(signDoc, 'additionalData.expiryDuration.value'),
            unit: get(signDoc, 'additionalData.expiryDuration.unit', ExpiryDurationUnits[0])
        }
        : null;
    const [showExpiryDuration, setShowExpiryDuration] = useState(!!initialExpiryDuration)
    const [expiryDuration, setExpiryDuration] = useState(initialExpiryDuration)

    // Hide finishByDuration picker for:
    // - Course types (FRONTAL_COURSE, ONLINE_COURSE) - they use sessionStartDate instead
    // - KNOWLEDGE_CENTER category - no deadline concept, just reference material
    const signDocSupportsFinishByDuration =
        isCourseType(selectedSignDocType) === false &&
        selectedCategory !== SignDocCategories.KNOWLEDGE_CENTER;

    // Finish by duration state for SignDoc completion deadline (days only)
    // NO_DURATION (-1) means "no deadline", positive values are days
    // Get existing value (edit mode) or default (create mode)
    const defaultFinishByDurationValue = (signDocSupportsFinishByDuration ? DEFAULT_DURATION_DAYS : NO_DURATION);
    const initialFinishByDuration = get(signDoc, 'finishByDuration') ?? defaultFinishByDurationValue;

    // Set finishByDuration state (for picker display, use DEFAULT when NO_DURATION)
    const [finishByDuration, setFinishByDuration] = useState(
        initialFinishByDuration === NO_DURATION ? DEFAULT_DURATION_DAYS : initialFinishByDuration
    );

    // 3. Set toggle state: ON if duration is set (positive value), OFF if NO_DURATION (-1)
    const [showFinishByDurationPicker, setShowFinishByDurationPicker] = useState(initialFinishByDuration !== NO_DURATION);

    // Group type state (controls versioning and recurring behavior)
    // SEQUENTIAL: One active version at a time, recurring creates new versions
    // PARALLEL: Multiple versions can be active simultaneously (default)
    const initialGroupType = get(signDoc, 'groupType', DEFAULT_GROUP_TYPE);
    const [groupType, setGroupType] = useState(initialGroupType);

    // recurringAt: Anchor date for SEQUENTIAL lifecycle (required on PARALLEL→SEQUENTIAL transition)
    const [recurringAt, setRecurringAt] = useState(null);
    const [isRecurringAtCalendarOpen, setIsRecurringAtCalendarOpen] = useState(false);

    // Detect PARALLEL→SEQUENTIAL transition in edit mode (requires mandatory date picker)
    const isTransitioningToSequential = editMode && initialGroupType === SIGNDOC_GROUP_TYPES.PARALLEL && groupType === SIGNDOC_GROUP_TYPES.SEQUENTIAL;

    // Recurring duration state (only relevant for SEQUENTIAL groupType)
    // Validate initial value - must have valid value and unit (MONTHS/YEARS only)
    const getValidRecurringDuration = () => {
        const duration = get(signDoc, 'recurringDuration');
        const isValid = get(duration, 'value') && RECURRING_DURATION_UNITS.includes(get(duration, 'unit'));
        return isValid ? duration : DEFAULT_RECURRING_DURATION;
    };
    const [recurringDuration, setRecurringDuration] = useState(() => getValidRecurringDuration());

    // SignDoc status state (ACTIVE or ARCHIVED)
    const [signDocStatus, setSignDocStatus] = useState(
        get(signDoc, 'status', DEFAULT_SIGNDOC_STATUS)
    );

    // Sequential active version conflict modal state
    // Used when trying to activate a version in SEQUENTIAL group when another is already active
    const [showSequentialConflictModal, setShowSequentialConflictModal] = useState(false);
    const [sequentialConflictInfo, setSequentialConflictInfo] = useState(null);

    // Generic error modal state - for errors that are not conflict errors
    const [showGenericErrorModal, setShowGenericErrorModal] = useState(false);
    const [genericErrorMessage, setGenericErrorMessage] = useState('');

    // Notification quota warning state
    const [showQuotaWarningModal, setShowQuotaWarningModal] = useState(false)
    const [quotaCheckData, setQuotaCheckData] = useState(null)

    // Check if groupType change is disabled:
    // - In edit mode when multiple versions already exist
    // - When creating a new version from existing SignDoc (will result in 2+ versions)
    const isGroupTypeChangeDisabled = (editMode && totalVersionCount > 1) || (isNewVersion && totalVersionCount > 0);

    // Get signDocType config functions through the hook
    const {
        getRequiresFileUpload,
        getRequiresLink,
        getFileUploadConfig,
        getShouldShowOrganizationChips,
        getCreateButtonText,
        getDocNameLabel,
        getDocDescriptionLabel,
        getDocDropLabel,
        getExtraSaveButtonText,
        getShouldShowAllowFastForwardCheckbox
    } = useSignDocTypes();

    const onLinkUrlChange = (e) => {
        const linkUrl = e.target.value || ''
        setLinkUrl(linkUrl)
        setLinkUrlError(null)
        if (selectedSignDocType === SignDocTypes.WATCH_AND_SIGN_YOUTUBE_LINK && !isYoutubeLink({linkUrl})) {
            setLinkUrlError(String(i18n.translate('newDocSign.modal.invalidLink')))
        } else if (selectedSignDocType === SignDocTypes.WATCH_AND_SIGN_VIMEO_LINK && !isVimeoLink({linkUrl})) {
            setLinkUrlError(String(i18n.translate('newDocSign.modal.invalidLink')))
        } else if (selectedSignDocType === SignDocTypes.CANVA_PRESENTATION && !isCanvaLink({linkUrl})) {
            setLinkUrlError(String(i18n.translate('newDocSign.modal.invalidLink')))
        } else if (signDocType === SignDocTypes.LINK) {
            setLinkUrlError(null)
        } else {
            setLinkUrlError(null)
        }
    }
    const onExamPassGradeChange = (e) => {
        if (e.target.value >= 0 && e.target.value <= 100) {
            setExamPassGrade(e.target.value)
        }
    }

    /**
     * Handles changes to expiry duration
     * @param {object|null} newDuration - Duration object {value: number, unit: string} or null
     */
    const onExpiryDurationChange = (newDuration) => {
        setExpiryDuration(newDuration);
    }

    /**
     * Handles changes to finish by duration (completion deadline)
     * Converts DurationPicker {value, unit} format to simple integer (days)
     * @param {object} params - Parameters object
     * @param {object|null} params.newDuration - Duration object {value: number, unit: string} or null
     */
    const onFinishByDurationChange = ({newDuration}) => {
        // DurationPicker returns {value, unit} but we only use days
        const days = newDuration?.value || DEFAULT_DURATION_DAYS;
        setFinishByDuration(days);
    }

    /**
     * Returns the finishByDuration value to save based on SignDoc support and toggle state
     * @returns {number} Duration in days or NO_DURATION (-1) if not applicable
     */
    const getFinishByDurationToSave = () => {
        return signDocSupportsFinishByDuration && showFinishByDurationPicker ? finishByDuration : NO_DURATION;
    }

    /**
     * Handles changes to recurring duration
     * Validates minimum frequency (1 month or 1 year depending on unit)
     * @param {object} newDuration - Duration object {value: number, unit: string}
     */
    const onRecurringDurationChange = (newDuration) => {
        // Validate minimum frequency
        const minValue = MIN_RECURRING_DURATION_VALUES[newDuration.unit];
        if (newDuration.value < minValue) {
            return; // Reject values below minimum
        }
        setRecurringDuration(newDuration);
    };

    /**
     * Returns the recurringDuration value to save based on groupType
     * @returns {object|null} Duration object if SEQUENTIAL, null if PARALLEL
     */
    const getRecurringDurationToSave = () => {
        return groupType === SIGNDOC_GROUP_TYPES.SEQUENTIAL ? recurringDuration : null;
    };

    /**
     * Handles search input change for user filter
     * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
     * @returns {void}
     */
    const onUserSearchChange = (e) => {
        setUserSearchQuery(e.target.value);
    }

    /**
     * Fetches display details for the selected users from the API.
     *
     * The API returns only the users the current admin is allowed to SEE (e.g. a Company Admin
     * sees fellow Company Admins, an Org Admin does not). This visible set is the single source
     * of truth for BOTH the "Selected" tab list and the selected count, so they always agree.
     *
     * @param {object} [params] - Parameters
     * @param {string[]} [params.userIds] - Explicit user IDs to fetch. Defaults to the user_* entries
     *   currently in selectedOrganizations. Passed explicitly on initial load to avoid stale state.
     * @returns {Promise<void>}
     */
    const fetchSelectedUsersDetails = async ({userIds} = {}) => {
        const selectedUserIds = userIds || Array.from(selectedOrganizations)
            .filter(id => id.startsWith('user_'))
            .map(id => id.replace('user_', ''));

        if (selectedUserIds.length === 0) {
            setSelectedUsersDetails([]);
            return;
        }

        setIsLoadingSelectedUsers(true);
        try {
            const response = await getUsersByIdsAPI({userIds: selectedUserIds});
            setSelectedUsersDetails(get(response, 'users', []));
        } catch (error) {
            console.error('Error fetching selected users details:', error);
            setSelectedUsersDetails([]);
        } finally {
            setIsLoadingSelectedUsers(false);
        }
    };

    /**
     * Toggles selection of an individual user from the "All Users" tab.
     *
     * Keeps selectedUsersDetails (the visible set that drives the count + "Selected" tab) in sync
     * optimistically, so the count updates instantly without an extra round-trip. Users in the
     * "All Users" list are already visibility-filtered by the server, so any user added here is
     * one the admin is allowed to see.
     *
     * @param {object} params - Parameters
     * @param {object} params.user - The user object (id, firstName, lastName, phone)
     * @param {boolean} params.isSelected - Whether the user is currently selected
     * @returns {Function} Click handler
     */
    const onToggleUser = ({user, isSelected}) => () => {
        const userOrgId = `user_${user.id}`;
        if (isSelected) {
            removeOrgFromSignDoc(userOrgId);
            setSelectedUsersDetails(prev => prev.filter(u => u.id !== user.id));
        } else {
            addOrgToSignDoc({organizationId: userOrgId});
            setSelectedUsersDetails(prev => prev.some(u => u.id === user.id) ? prev : [...prev, user]);
        }
        setQuotaCheckData(null);
    };

    /**
     * Removes a user from the selected set and immediately removes them from the displayed list.
     * @param {string} userId - The user ID (without user_ prefix)
     */
    const removeSelectedUser = (userId) => {
        const userOrgId = `user_${userId}`;
        removeOrgFromSignDoc(userOrgId);
        setSelectedUsersDetails(prev => prev.filter(u => u.id !== userId));
    };

    /**
     * Handles sub-tab change within the Individual Users section.
     * Fetches selected user details when switching to the "Selected" tab.
     * @param {string} tabValue - The new tab value ('allUsers' or 'selectedUsers')
     */
    const onSelectedUsersSubTabChange = (tabValue) => {
        // No fetch here — selectedUsersDetails is loaded once (edit mode) and then kept in sync
        // client-side as users are added/removed/imported. Just reset to the first page on open.
        if (tabValue === 'selectedUsers') {
            setSelectedUsersPage(0);
        }
    };

    /**
     * Handles file selection for importing users from CSV/Excel.
     * Parses the file, looks up users by phone number, and sets preview data.
     * The file must have columns: Phone, First Name, Last Name (standard format from importUsersFromFile).
     *
     * @param {React.ChangeEvent<HTMLInputElement>} e - The file input change event
     * @returns {Promise<void>}
     */
    const handleImportFileSelected = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reset file input for re-selection of same file
        e.target.value = '';

        setIsProcessingImport(true);
        setImportPreviewData(null);

        try {
            // Parse file using existing utility with default required columns: Phone, First Name, Last Name
            const { data } = await importUsersFromFile({
                file,
                companyCountryCode: companyContext.countryCode
            });

            // Extract unique phone numbers from parsed data
            const phoneNumbers = uniq(
                filter(
                    map(keys(data), rowIndex => data[rowIndex].phone),
                    phone => phone && phone.trim()
                )
            );

            if (phoneNumbers.length === 0) {
                alert(i18n.translate('signDocModal.importUsers.noMatchesFound'));
                setIsProcessingImport(false);
                return;
            }

            // Call API to lookup users by phone numbers
            const response = await lookupUsersByPhonesAPI({ phoneNumbers });
            const matchedUsers = response.matchedUsers || [];

            // Create a set of matched phone numbers normalized to E164 for comparison
            // DB phones might be in legacy format (0545831988) while file phones are E164 (+972545831988)
            const matchedPhonesE164 = new Set(
                matchedUsers.map(user => formatPhoneToE164(user.phone, companyContext.countryCode))
            );

            // Identify unmatched rows from the parsed data
            const unmatchedRows = [];
            keys(data).forEach(rowIndex => {
                const row = data[rowIndex];
                // Normalize file phone to E164 for comparison
                const filePhoneE164 = formatPhoneToE164(row.phone, companyContext.countryCode);
                if (row.phone && !matchedPhonesE164.has(filePhoneE164)) {
                    unmatchedRows.push({
                        rowIndex: parseInt(rowIndex, 10),
                        phone: row.phone,
                        firstName: row.firstName || '',
                        lastName: row.lastName || '',
                        email: row.email || '',
                        idNumber: row.idNumber || '',
                        employeeId: row.employeeId || ''
                    });
                }
            });

            // Set preview data for ImportUsersForAssignment component
            setImportPreviewData({
                matchedUsers,
                unmatchedRows
            });
        } catch (error) {
            console.error('Error importing users from file:', error);
            // Handle validation errors from importUsersFromFile (missing columns, etc.)
            if (error && error.message) {
                alert(error.message);
            } else {
                alert(i18n.translate('signDocModal.importUsers.fileError'));
            }
        } finally {
            setIsProcessingImport(false);
        }
    };

    /**
     * Handles confirmation of imported users.
     * Merges the selected users into the selectedOrganizations Set.
     *
     * @param {string[]} userIds - Array of user IDs to add to selection
     * @returns {void}
     */
    const handleImportUsersConfirm = (userIds) => {
        setSelectedOrganizations(prev => {
            const newSet = new Set(prev);
            userIds.forEach(id => newSet.add(`user_${id}`));
            return newSet;
        });

        // Add the imported users to the visible set client-side using the objects we already have from
        // the import lookup (importPreviewData.matchedUsers) — no refetch needed. This keeps the count
        // and "Selected" tab in sync.
        const matchedById = new Map((importPreviewData?.matchedUsers || []).map(u => [u.id, u]));
        setSelectedUsersDetails(prev => {
            const existingIds = new Set(prev.map(u => u.id));
            const additions = userIds
                .filter(id => !existingIds.has(id))
                .map(id => matchedById.get(id))
                .filter(Boolean);
            return additions.length > 0 ? [...prev, ...additions] : prev;
        });

        setImportPreviewData(null);
    };

    /**
     * Handles cancellation of the import preview.
     * Clears the preview data without adding any users.
     *
     * @returns {void}
     */
    const handleImportCancel = () => {
        setImportPreviewData(null);
    };

    /**
     * Handles search input change for organization filter
     * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
     * @returns {void}
     */
    const onOrgSearchChange = (e) => {
        setOrgSearchQuery(e.target.value);
    }

    /**
     * Checks if at least one organization is selected for SignDoc types that require organization assignment
     * @returns {boolean} True if at least one organization is selected, false otherwise
     */
    const hasSelectedOrganizations = () => {
        return !isEmpty(selectedOrganizations);
    }

    /**
     * Checks if organization assignment is required for the current SignDoc type
     * @returns {boolean} True if organization assignment is required, false otherwise
     */
    const requiresOrganizationAssignment = () => {
        // Course types (FRONTAL_COURSE, ONLINE_COURSE) don't require org assignment
        // They can be created without orgs and assigned later
        if (isCourseType()) {
            return false;
        }
        // In edit mode, organization assignment is always required when chips are shown
        if (editMode) {
            return toShowOrganizationChips;
        }
        // For new SignDocs, check if the type requires organization assignment
        return getShouldShowOrganizationChips({ type: selectedSignDocType });
    }

    // Helper function to check if SignDoc requires assignment warning
    // For now, we'll show warning for all types in edit mode when assigning to organizations
    // Close organization assignment warning modal
    const closeOrganizationAssignmentWarningModal = () => {
        setOrganizationAssignmentWarningModal(false)
    }

    // Handle organization assignment warning confirmation
    const handleOrganizationAssignmentWarningConfirm = async () => {
        closeOrganizationAssignmentWarningModal()
        // Proceed with saving without organization assignment
        // checkQuotaAndSave is a no-op here (no orgs selected, quota guard short-circuits)
        // but keeps the call chain consistent — all save paths route through the quota gateway
        await checkQuotaAndSave({})
    }

    const onDocNameChange = (e) => {
        setDocName(e.target.value)
    }
    const onDocDescriptionChange = (e) => {
        setDocDescription(e.target.value)
    }
    /**
     * Handles file upload with user messaging for video uploads
     * @param {object} params - The function parameters container object.
     * @param {string} params.signDocId - The ID of the SignDoc to upload files for.
     * @returns {Promise<void>} A promise that resolves when upload is complete.
     */
    const uploadFiles = async ({signDocId}) => {
        if (files && files.length) {
            try {
                // Set upload status to uploading for video files
                await updateSignDocUploadStatusAPI({signDocId, uploadStatus: SignDocUploadStatus.UPLOADING})
                // Trigger list refresh to show the uploaded document
                refreshSignDocList()
                const uploadResult = await uploadDocumentFileAPI({files, onProgress: localOnProgress, documentId: signDocId})
                // wrapError in rest.js resolves errors instead of rejecting — check result for failure.
                // - error === true: MyError server errors (quota, validation, etc.) from data.meta
                // - httpCode >= 400: non-MyError server errors (S3 failure, etc.) from standardize()
                // - isAxiosError: network errors (raw Axios error object, no server response)
                if (uploadResult?.error === true || uploadResult?.httpCode >= 400 || uploadResult?.isAxiosError) {
                    throw uploadResult
                }

                // Upload completed successfully - status is set to 'ready' by backend
                // Trigger list refresh to show the updated document
                refreshSignDocList()
            } catch (error) {
                console.error('Upload failed:', error)
                // Upload status is set to FAILED by the backend upload endpoint on failure.
                // No need to set it here — and doing so can trigger a redundant error dialog.
                throw error // Re-throw to handle in calling function
            }
        }
    }

    const handleAssignmentWarningModalToAppear = async () => {
        // Check if organization assignment is required but no organizations are selected
        if (requiresOrganizationAssignment() && !hasSelectedOrganizations()) {
            setOrganizationAssignmentWarningModal(true)
            return
        }
        await checkQuotaAndSave({})
    }

    /**
     * Checks notification quota before saving. If notifications are enabled and quota is insufficient,
     * shows a warning modal. Otherwise proceeds with save.
     * @param {object} params - The function parameters
     * @param {boolean} [params.withRoute=false] - Whether to navigate after save
     * @param {boolean} [params.forceSignDocSingleActiveVersion=false] - Force single active version
     */
    const checkQuotaAndSave = async ({withRoute = false, forceSignDocSingleActiveVersion = false}) => {
        if (notifyOrganizationUserOnNewTask && selectedMessagingTypes.length > 0 && selectedOrganizations.size > 0) {
            try {
                setIsLoading(true)
                const quotaResult = await checkNotificationQuotaAPI({
                    organizationIds: Array.from(selectedOrganizations).filter(id => !id.startsWith('user_')),
                    messagingTypes: selectedMessagingTypes,
                    signDocId: editMode ? signDoc?.id : undefined
                })
                setIsLoading(false)

                if (!quotaResult.canSend) {
                    setQuotaCheckData(quotaResult)
                    setShowQuotaWarningModal(true)
                    return
                }
            } catch (error) {
                setIsLoading(false)
                console.error('Quota check failed:', error)
                const errorMessage = get(error, 'errorMessage') || get(error, 'message') || i18n.translate('errors.errorMessageDefault')
                setGenericErrorMessage(errorMessage)
                setShowGenericErrorModal(true)
                return
            }
        }
        await createOrEditDocument({withRoute, forceSignDocSingleActiveVersion})
    }

    /**
     * Creates a new SignDoc or updates an existing one, including associated questionnaire data if applicable.
     *
     * @param {object} params - The function parameters container object.
     * @param {boolean} [params.withRoute=false] - Indicates if the action should trigger a route change after completion.
     * @param {boolean} [params.isEditable=true] - Determines if the SignDoc should be editable after creation/update.
     * @param {boolean} [params.forceSignDocSingleActiveVersion=false] - If true, archives existing active version in SEQUENTIAL group.
     * @returns {Promise<void>} A promise that resolves when the document creation or update process is complete.
     */
    const createOrEditDocument = async ({withRoute = false, forceSignDocSingleActiveVersion = false}) => {
        setIsLoading(true)
        let tmpSignDoc = signDoc
          // For UPLOAD_DOCUMENT, FRONTAL_COURSE, and ONLINE_COURSE types, add approver parameters
          const supportsApprovers = SignDocTypesThatSupportApprovals.includes(selectedSignDocType);
          const approverParams = supportsApprovers ? {
            approverUserIds: map(selectedApproverUsers, 'value'),
            enableApprovals: enableApprovals
        } : {};

        // For COURSE types, add course-specific parameters to additionalData
        const courseParams = isCourseType() ? {
            additionalData: {
                sessionStartDate: sessionStartDate,
                location: courseLocation || null
            }
        } : {};

        // For LEARNING_PATH type, pass the free-path mode flag as a top-level API param
        // (mirrors enableApprovals). Backend createSignDoc/updateSignDoc nests it into
        // additionalData.unorderedLearningPathTasks. Sending it nested in additionalData
        // here would clobber any other keys when spread into the payload.
        const learningPathParams = selectedSignDocType === SignDocTypes.LEARNING_PATH
            ? { unorderedLearningPathTasks }
            : {};

        // Send isMustDo only when the company has the FF - the backend rejects it otherwise
        const mustDoParams = hasMustDoComplianceFF ? { isMustDo } : {};

        // Prepare expiry duration for CERTIFICATION category
        const expiryDurationToSave = (selectedCategory === SignDocCategories.CERTIFICATION && showExpiryDuration && expiryDuration && expiryDuration.value > 0)
            ? expiryDuration
            : null;

        // Determine if this is a file-upload type in create mode.
        // For these types, we must: create record without assignments -> upload file -> assign users.
        // This prevents users from being notified about documents with no uploaded file.
        const requiresFileUpload = getRequiresFileUpload({ type: selectedSignDocType });
        const isFileUploadCreate = !editMode && requiresFileUpload;

        try {
        if (editMode) {
            await updateSignDocAPI({
                docName,
                docDescription,
                linkUrl,
                category: selectedCategory,
                selectedOrganizationIds: Array.from(selectedOrganizations),
                signDocId: tmpSignDoc.id,
                notifyOrganizationUserOnNewTask,
                notifyMessagingTypes: selectedMessagingTypes,
                forceCompletion: selectedCategory === SignDocCategories.FORCE_COMPLETION,
                allowFastForward,
                ...approverParams,
                ...courseParams,
                ...learningPathParams,
                ...mustDoParams,
                expiryDuration: expiryDurationToSave,
                finishByDuration: getFinishByDurationToSave(),
                groupType,
                recurringDuration: getRecurringDurationToSave(),
                recurringAt: isTransitioningToSequential ? recurringAt : undefined,
                status: signDocStatus,
                forceSignDocSingleActiveVersion
            })
        } else {

            tmpSignDoc = await createSignDocAPI({
                docName,
                docDescription,
                linkUrl,
                type: selectedSignDocType,
                category: selectedCategory,
                // For file-upload types, create the record without assignments.
                // Organizations will be assigned after the file upload succeeds.
                selectedOrganizationIds: isFileUploadCreate ? [] : Array.from(selectedOrganizations),
                notifyOrganizationUserOnNewTask,
                notifyMessagingTypes: selectedMessagingTypes,
                forceCompletion: selectedCategory === SignDocCategories.FORCE_COMPLETION,
                allowFastForward,
                ...approverParams,
                ...courseParams,
                ...learningPathParams,
                ...mustDoParams,
                expiryDuration: expiryDurationToSave,
                finishByDuration: getFinishByDurationToSave(),
                groupType,
                recurringDuration: getRecurringDurationToSave(),
                status: signDocStatus,
                groupRootId: get(signDoc, 'groupRootId'),
                forceSignDocSingleActiveVersion
            })
        }

        // Invalidate React Query cache for SignDocs (all parameter combinations)
        invalidateQueries({queryKeys: [
            queryKeys.uniqueSignDocs(false),
            queryKeys.uniqueSignDocs(true),
            queryKeys.allAdminSignDocs(false),
            queryKeys.allAdminSignDocs(true),
            queryKeys.adminCreatedSignDocTypes(false),
            queryKeys.adminCreatedSignDocTypes(true)
        ]});

        // Handle subtypes for all SignDoc types that require them
        if (hasSubtype({ signDocType: tmpSignDoc.type })) {
            const subtypeData = {
                examPassGrade: tmpSignDoc.type === SignDocTypes.EXAM ? examPassGrade : undefined,
                isAnonymous: tmpSignDoc.type === SignDocTypes.SURVEY ? isAnonymous : false,
            };
            if (editMode) {
                await updateSubtype({
                    signDocType: tmpSignDoc.type,
                    signDocId: get(tmpSignDoc, 'id'),
                    subtypeData
                });
            } else {
                await createSubtype({
                    signDocType: tmpSignDoc.type,
                    signDocId: get(tmpSignDoc, 'id'),
                    subtypeData
                });
            }
        }

        // File-upload create mode: await upload, then assign organizations
        if (isFileUploadCreate) {
            try {
                await uploadFiles({signDocId: tmpSignDoc.id});
            } catch (uploadError) {
                // Upload failed — delete the orphaned SignDoc record and show error
                console.error('File upload failed, deleting orphaned SignDoc:', uploadError);
                try {
                    await deleteSignDocAPI({signDocId: tmpSignDoc.id}, {handleErrorByComponent: true});
                } catch (deleteError) {
                    console.error('Failed to delete orphaned SignDoc after upload failure:', deleteError);
                }
                invalidateQueries({queryKeys: [
                    queryKeys.uniqueSignDocs(false),
                    queryKeys.uniqueSignDocs(true),
                    queryKeys.allAdminSignDocs(false),
                    queryKeys.allAdminSignDocs(true),
                    queryKeys.adminCreatedSignDocTypes(false),
                    queryKeys.adminCreatedSignDocTypes(true)
                ]});
                setIsLoading(false)
                // Show our custom error dialog only if the global interceptor didn't handle it
                // (e.g., network errors, timeouts — no server response)
                const hasServerResponse = uploadError?.response?.status || uploadError?.error
                if (!hasServerResponse) {
                    setShowUploadError(true)
                }
                return;
            }

            // Fire allocation in the background (don't block UI)
            updateSignDocAPI({
                docName,
                docDescription,
                linkUrl,
                category: selectedCategory,
                selectedOrganizationIds: Array.from(selectedOrganizations),
                signDocId: tmpSignDoc.id,
                notifyOrganizationUserOnNewTask,
                notifyMessagingTypes: selectedMessagingTypes,
                forceCompletion: selectedCategory === SignDocCategories.FORCE_COMPLETION,
                allowFastForward,
                ...approverParams,
                ...courseParams,
                ...learningPathParams,
                expiryDuration: expiryDurationToSave,
                finishByDuration: getFinishByDurationToSave()
            })
                .then(() => {
                    invalidateQueries({queryKeys: [
                        queryKeys.uniqueSignDocs(false),
                        queryKeys.uniqueSignDocs(true),
                        queryKeys.allAdminSignDocs(false),
                        queryKeys.allAdminSignDocs(true),
                        queryKeys.adminCreatedSignDocTypes(false),
                        queryKeys.adminCreatedSignDocTypes(true)
                    ]});
                })
                .catch(error => console.error('Background allocation failed:', error))
        }

        setIsLoading(false)
        onAfterEditComplete && onAfterEditComplete()
        closeNewDocSignModal()

        // Handle navigation - navigate to edit page if creating or creating new version
        if (!editMode || withRoute) {
            navigateAfterSignDocSave({
                signDocType: tmpSignDoc.type,
                signDocCategory: tmpSignDoc.category || selectedCategory,
                signDocId: tmpSignDoc.id,
                navigateToTab: false
            });
        }
        } catch (error) {
            // Handle errors using normalized structure {error, params, errorMessage} from interceptor
            const errorType = get(error, 'error');

            // Handle SEQUENTIAL_ACTIVE_VERSION_EXISTS error - show conflict confirmation modal
            if (errorType === SignDocErrorCodes.SEQUENTIAL_ACTIVE_VERSION_EXISTS) {
                const conflictData = get(error, 'params', {});
                setSequentialConflictInfo(conflictData);
                setShowSequentialConflictModal(true);
            } else {
                // For all other errors - show generic error modal with error message
                const errorMessage = get(error, 'errorMessage') || get(error, 'message') || i18n.translate('errors.errorMessageDefault');
                setGenericErrorMessage(errorMessage);
                setShowGenericErrorModal(true);
            }
            setIsLoading(false)
        }
    }

    /**
     * Handles user declining the sequential conflict - close modal and stay on form
     */
    const handleSequentialConflictDecline = () => {
        setShowSequentialConflictModal(false);
        setSequentialConflictInfo(null);
        // User stays on modal, can change status to ARCHIVED or edit other fields
    };

    /**
     * Handles user agreeing to archive other version - retry with force flag
     */
    const handleSequentialConflictAgree = async () => {
        setShowSequentialConflictModal(false);
        setSequentialConflictInfo(null);
        // Retry save with force flag
        await createOrEditDocument({forceSignDocSingleActiveVersion: true});
    };

    const onSelectedDocFiles = ({files}) => {
        setFiles(files)
    }

    const localOnProgress = (event) => {
        const {progress} = onProgress(event)
        setProgress(progress)
        // Modal close is handled by createOrEditDocument after all steps complete.
        // Do not close here — upload reaching 100% does not mean assignment is done.
    }
    /**
     * Handles organization/user chip click - toggles selection
     * @param {object} params - Parameters
     * @param {string} params.organizationId - Organization ID (or user_xxx for personal orgs)
     * @param {boolean} params.isSelected - Whether the org/user is currently selected
     * @returns {Function} Click handler function
     */
    const onClickOrganization = ({organizationId, isSelected}) => () => {
        if (isSelected) {
            removeOrgFromSignDoc(organizationId)
        } else {
            addOrgToSignDoc({organizationId})
        }
        setQuotaCheckData(null)
    }
    /**
     * Adds organization or user to SignDoc assignment
     * @param {object} params - Parameters
     * @param {string} params.organizationId - Organization ID (or user_xxx for personal orgs)
     */
    const addOrgToSignDoc = ({organizationId}) => {
        const updatedSelectedOrgs = new Set(selectedOrganizations)
        updatedSelectedOrgs.add(organizationId)
        setSelectedOrganizations(updatedSelectedOrgs)
    }

    /**
     * Removes an organization or user from SignDoc assignments
     * @param {string} organizationId - Organization ID (or user_xxx for VPOs)
     */
    const removeOrgFromSignDoc = (organizationId) => {
        const updatedSelectedOrgs = new Set(selectedOrganizations)
        updatedSelectedOrgs.delete(organizationId)
        setSelectedOrganizations(updatedSelectedOrgs)
    }

    /**
     * Checks if all organizations from the database are currently selected.
     * CRITICAL: Must check against total DB count (orgTotalCount), not just loaded page!
     * @returns {boolean} True if all organizations are selected, false otherwise
     */
    const areAllOrganizationsSelected = useMemo(() => {
        // Count only organization selections (exclude user_xxx keys)
        const selectedOrgCount = Array.from(selectedOrganizations).filter(id => !id.startsWith('user_')).length
        return selectedOrgCount === orgTotalCount && orgTotalCount > 0
    }, [selectedOrganizations, orgTotalCount])

    /**
     * Counts the selected personal users the current admin can SEE.
     *
     * Derived from selectedUsersDetails (the server's visibility-filtered set) rather than the raw
     * selectedOrganizations set, so the count always matches the users shown in the "Selected" tab.
     * A Company/Super Admin includes fellow Company Admins; an Org Admin does not. The full
     * selectedOrganizations set is still what gets saved, so hidden assignments are preserved.
     * @returns {number} Count of visible selected personal users
     */
    const selectedPersonalUsersCount = useMemo(() => {
        return selectedUsersDetails.length
    }, [selectedUsersDetails])

    /**
     * Counts the number of selected organizations (non-user keys in selectedOrganizations).
     * Used for displaying selected count in organization ChipsPaginationWrapper.
     * @returns {number} Count of selected organizations
     */
    const selectedOrganizationsCount = useMemo(() => {
        return Array.from(selectedOrganizations).filter(id => !id.startsWith('user_')).length
    }, [selectedOrganizations])

    /**
     * Removes ALL organizations from the SignDoc.
     * Uses allAdminOrganizations to remove all orgs from ALL pages, not just current page.
     * @returns {void}
     */
    const removeAllOrganizations = () => {
        const updatedSelectedOrgs = new Set(selectedOrganizations);

        // Remove all organizations from the existing data
        // allAdminOrganizations has ALL orgs loaded (not paginated)
        if (allAdminOrganizations) {
            allAdminOrganizations.forEach(org => {
                const organizationId = get(org, 'organization.id');
                if (organizationId) {
                    updatedSelectedOrgs.delete(organizationId);
                }
            });
        }

        setSelectedOrganizations(updatedSelectedOrgs);
    }

    /**
     * Handles clicking the "assign to all organizations" chip
     * Either assigns all organizations or removes all organizations
     * @returns {void}
     */
    const onClickAssignToAll = () => {
        if (areAllOrganizationsSelected) {
            removeAllOrganizations()
        } else {
            assignToAllOrganizations()
        }
    }

    /**
     * Assigns ALL organizations from the database to the SignDoc.
     * Uses the existing allAdminOrganizations data (already loaded by useUserAdminOrgs).
     * @returns {void}
     */
    const assignToAllOrganizations = () => {
        const updatedSelectedOrgs = new Set(selectedOrganizations);

        // Add all organizations from the existing data
        // allAdminOrganizations already has ALL orgs loaded (not paginated)
        if (allAdminOrganizations) {
            allAdminOrganizations.forEach(org => {
                const organizationId = get(org, 'organization.id');
                if (organizationId) {
                    updatedSelectedOrgs.add(organizationId);
                }
            });
        }

        setSelectedOrganizations(updatedSelectedOrgs);
    }

    /**
     * Validates if a URL is a recognized online meeting platform link.
     * @param {string} url - The URL to validate
     * @returns {object} Object with isValid boolean and platform name if valid
     */
    const validateMeetingLink = (url) => {
        if (!url || typeof url !== 'string') {
            return { isValid: false, platform: null };
        }

        const trimmedUrl = url.trim().toLowerCase();

        try {
            // Allow URLs without protocol
            const urlToTest = trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')
                ? trimmedUrl
                : `https://${trimmedUrl}`;

            const urlObj = new URL(urlToTest);
            const hostname = urlObj.hostname.toLowerCase();

            // Import shared meeting platforms list from SignDocLib


            // Check if hostname matches any recognized platform
            for (const platform of MEETING_PLATFORMS) {
                for (const pattern of platform.patterns) {
                    if (matchesPlatformPattern(hostname, pattern)) {
                        return { isValid: true, platform: platform.name };
                    }
                }
            }

            return { isValid: false, platform: null };
        } catch (error) {
            return { isValid: false, platform: null };
        }
    };

    /**
     * Validates course-specific fields
     * sessionStartDate is optional - courses can be created first and date added later
     * @returns {boolean} True if all course fields are valid
     */
    const validateCourseFields = () => {
        if (!isCourseType()) {
            return true; // Not a course type, validation passes
        }

        // sessionStartDate is optional - courses can be created without a date set

        // Required: location (physical address or meeting link)
        if (!courseLocation || courseLocation.trim().length === 0) {
            return false;
        }

        // Additional validation for ONLINE_COURSE: must be a valid meeting link
        if (isOnlineCourse()) {
            const meetingLinkValidation = validateMeetingLink(courseLocation);
            if (!meetingLinkValidation.isValid) {
                return false;
            }
        }

        // All course validations passed
        return true;
    };

    const canSubmit = useMemo(() => {
        if (linkUrlError) {
            return false
        }

        // PARALLEL→SEQUENTIAL transition requires a future date
        if (isTransitioningToSequential && !recurringAt) {
            return false;
        }

        // Validate course-specific fields
        if (!validateCourseFields()) {
            return false;
        }

        // Use the configuration functions to determine requirements
        if (getRequiresFileUpload({ type: selectedSignDocType })) {
            if (editMode) {
                return docName.trim().length > 0
            }
            return docName.trim().length > 0 && files && files.length > 0
        } else if (getRequiresLink({ type: selectedSignDocType })) {
            return docName.trim().length > 0 && linkUrl.trim().length > 0 && !linkUrlError
        } else {
            return docName.trim().length > 0
        }
    }, [linkUrlError, selectedOrganizations, docName, files, linkUrl, editMode, selectedSignDocType, courseLocation, isTransitioningToSequential, recurringAt])

    useEffect(() => {
        if (editMode || isNewVersion) {
            let text;
            if(selectedCategory === SignDocCategories.FORCE_COMPLETION){
                text = i18n.translate(`signDocTypes.${signDoc.type}`)
            }
            else{
                text = i18n.translate(`signDocTypesNotForCompletion.${signDoc.type}`)
            }
            setSignDocOptions([{
                value: signDoc.type,
                text
            }])
        } else {
            setSignDocOptions(map(SignDocOptionsTypes, (type) => {
                let text;
                if(selectedCategory === SignDocCategories.FORCE_COMPLETION){
                    text = i18n.translate(`signDocTypes.${type}`)
                }
                else{
                    text = i18n.translate(`signDocTypesNotForCompletion.${type}`)
                }
                return {
                    value: type,
                    text
                }
            }))
        }

    }, [selectedCategory]);

    /**
     * Initialize selected organizations in edit mode (runs once on mount).
     * CRITICAL: Only runs once to avoid resetting selections on page changes!
     */
    useEffect(() => {
        if (editMode && signDoc) {
            const selectedOrgsSet = new Set();

            // In edit mode, add valid organization assignments to selected set
            map(signDoc.organizationSignDoc, (org) => {
                if (org.isValid) {
                    // VPOs use user_xxx key, regular orgs use OrganizationId
                    const linkedUserId = get(org, 'organization.linkedUserId');
                    const orgKey = linkedUserId ? `user_${linkedUserId}` : org.OrganizationId;
                    selectedOrgsSet.add(orgKey);
                }
            });

            setSelectedOrganizations(selectedOrgsSet);

            // Eagerly load the visible selected users so the count (derived from this set) is
            // correct immediately, before the "Selected" sub-tab is opened. Pass the IDs explicitly
            // since selectedOrganizations state isn't updated synchronously here.
            const initialUserIds = Array.from(selectedOrgsSet)
                .filter(id => id.startsWith('user_'))
                .map(id => id.replace('user_', ''));
            if (initialUserIds.length > 0) {
                fetchSelectedUsersDetails({userIds: initialUserIds});
            }
        }
        setIsLoading(false);
    }, [editMode, signDoc]); // Only depends on editMode and signDoc, NOT on paginatedOrganizations

    /**
     * Update availableOrganizations when paginated data changes.
     * CRITICAL: Does NOT reset selectedOrganizations!
     */
    useEffect(() => {
        setAvailableOrganizations(paginatedOrganizations || []);
    }, [paginatedOrganizations]);

    /**
     * Handle user pagination errors from the hook.
     * Shows error dialog when user fetch fails.
     */
    useEffect(() => {
        if (userError) {
            console.error('Error fetching users:', userError);
            setShowUserPaginationError(true);
        }
    }, [userError]);

    /**
     * Handle organization pagination errors from the hook.
     * Shows error dialog when organization fetch fails.
     */
    useEffect(() => {
        if (orgError) {
            console.error('Error fetching organizations:', orgError);
            setShowOrgPaginationError(true);
        }
    }, [orgError]);

    /**
     * Reset import preview data when modal closes.
     * Ensures clean state when modal is reopened.
     */
    useEffect(() => {
        if (!newDocSignModalOpen) {
            setImportPreviewData(null);
            setIsProcessingImport(false);
        }
    }, [newDocSignModalOpen]);

    /**
     * Fetch version count for edit mode to determine if category can be changed.
     * Category change is only allowed for single-version SignDocs.
     */
    useEffect(() => {
        const fetchVersionCount = async () => {
            // Only fetch in edit mode (not for new SignDocs or new versions)
            if (!editMode || !signDoc?.id || isNewVersion) {
                setVersionCount(null);
                return;
            }

            setIsLoadingVersionCount(true);
            try {
                const versions = await getSignDocVersionsAPI({ signDocId: signDoc.id });
                setVersionCount(get(versions, 'length', 1));
            } catch (error) {
                console.error('Error fetching version count:', error);
                // Default to multi-version (safe default - keeps category disabled)
                setVersionCount(2);
            } finally {
                setIsLoadingVersionCount(false);
            }
        };

        fetchVersionCount();
    }, [editMode, signDoc?.id, isNewVersion]);


    const onSelectedSignDocTypeOption = (e) => {
        const newSelectedSignDocType = e.target.value
        setSelectedSignDocType(newSelectedSignDocType)
        if (newSelectedSignDocType === SignDocTypes.WATCH_AND_SIGN_YOUTUBE_LINK) {
            if (linkUrl && !isYoutubeLink({linkUrl})) {
                setLinkUrlError(String(i18n.translate('newDocSign.modal.invalidLink')))
            } else {
                setLinkUrlError(null)
            }
        } else if (newSelectedSignDocType === SignDocTypes.WATCH_AND_SIGN_VIMEO_LINK) {
            if (linkUrl && !isVimeoLink({linkUrl})) {
                setLinkUrlError(String(i18n.translate('newDocSign.modal.invalidLink')))
            } else {
                setLinkUrlError(null)
            }
        } else if (newSelectedSignDocType === SignDocTypes.CANVA_PRESENTATION) {
            if (linkUrl && !isCanvaLink({linkUrl})) {
                setLinkUrlError(String(i18n.translate('newDocSign.modal.invalidLink')))
            } else {
                setLinkUrlError(null)
            }
        } else {
            setLinkUrlError(null)
        }
    }

    const onSelectCategory = (e) => {
        const newCategory = e.target.value;
        setSelectedCategory(newCategory);

        // Clear expiry duration if switching away from CERTIFICATION
        if (newCategory !== SignDocCategories.CERTIFICATION) {
            setShowExpiryDuration(false);
            setExpiryDuration(null);
        }

        // Disable notifications for KNOWLEDGE_CENTER
        if (newCategory === SignDocCategories.KNOWLEDGE_CENTER) {
            setNotifyOrganizationUserOnNewTask(false);
        }
    }

    /**
     * Determines if category dropdown should be disabled.
     * Category can only be changed for single-version SignDocs in edit mode.
     * @returns {boolean} True if category should be disabled
     */
    const isCategoryDisabled = () => {
        // Disabled if only one category option available
        if (getCategoryOptions(companyContext, allowedCategories, i18n).length <= 1) return true;
        // Always disabled when creating new version
        if (isNewVersion) return true;
        // Not disabled in create mode
        if (!editMode) return false;
        // In edit mode: disabled if multi-version group OR still loading
        return isLoadingVersionCount || versionCount !== 1;
    };

    /**
     * Gets tooltip text for disabled category dropdown.
     * @returns {string|null} Tooltip text or null if no tooltip needed
     */
    const getCategoryDisabledTooltip = () => {
        if (!isCategoryDisabled()) return null;
        if (isNewVersion) return null; // No special tooltip for new version case
        if (versionCount > 1) {
            return i18n.translate('certifications.categoryChangeDisabled.multipleVersions');
        }
        return null;
    };

    const onSendNewTaskForUserChange = (e) => {
        setNotifyOrganizationUserOnNewTask(e.target.checked)
        setQuotaCheckData(null)
    }

    const onAllowFastForwardChange = (e) => {
        setAllowFastForward(e.target.checked)
    }

    /**
     * Handles messaging type selection with multi-select toggle behavior.
     * Uses same pattern as SendNotificationsModal.
     * @param {string} messagingType - The clicked message type
     */
    const onSelectedMessagingTypeChange = (messagingType) => {
        if (selectedMessagingTypes.includes(messagingType) && selectedMessagingTypes.length > 1) {
            // Remove if already selected (but keep at least one)
            setSelectedMessagingTypes(selectedMessagingTypes.filter((type) => type !== messagingType))
        } else {
            // Add if not selected, deduplicate
            let messagingTypes = [...selectedMessagingTypes, messagingType]
            messagingTypes = messagingTypes.filter((value, index, self) => self.indexOf(value) === index)
            setSelectedMessagingTypes(messagingTypes)
        }
        setQuotaCheckData(null)
    }

    // Get document-specific button text using the hook
    const getSignDocButtonText = useMemo(() => {
        if (editMode) {
            return i18n.translate('general.saveChanges')
        } else {
            // Use type-specific button text from signDocTypeConfig via hook
            return getCreateButtonText({ type: selectedSignDocType });
        }
    }, [selectedSignDocType, editMode, getCreateButtonText]);

    // Get extra save button text if needed
    const extraSaveButtonText = useMemo(() => {
        return getExtraSaveButtonText({ type: selectedSignDocType });
    }, [selectedSignDocType, getExtraSaveButtonText]);

    // Determine whether to show organization chips
    const toShowOrganizationChips = useMemo(() => {
        if (editMode) return true;
        return getShouldShowOrganizationChips({ type: selectedSignDocType });
    }, [selectedSignDocType, editMode, getShouldShowOrganizationChips]);

    // Get file upload configuration based on SignDoc type using the hook
    const fileUploadConfig = useMemo(() => {
        return getFileUploadConfig({ type: selectedSignDocType });
    }, [selectedSignDocType, getFileUploadConfig]);


    // Assignment tabs for organizations and individual users
    const assignmentTabs = useMemo(() => {
        const organizationsTab = {
            value: 'organizations',
            label: i18n.translate('newDocSign.modal.assignmentTabs.organizations'),
            content: (
                <div className={`organizationChipsWrapper ${requiresOrganizationAssignment() && !hasSelectedOrganizations() ? 'noOrganizationsSelected' : ''}`}>
                    {/* Organization search input */}
                    <FormInput
                        dontShowErrorMessage
                        id="orgSearch"
                        label={i18n.translate('learningManagement.createEditNewSignDocModal.organizationsSection.searchLabel')}
                        className="orgSearchInput"
                        value={orgSearchQuery}
                        onChange={onOrgSearchChange}
                        placeholder={i18n.translate('learningManagement.createEditNewSignDocModal.organizationsSection.searchPlaceholder')}
                    />

                    {/* Assign to All chip - shown when there are multiple orgs */}
                    {orgTotalCount > 1 && (
                        <div className="assignmentChip assignToAllChip" key="assign-to-all">
                            <Chip
                                className={`assignToAllOrganizationsChip ${areAllOrganizationsSelected ? 'remove' : 'add'}`}
                                deleteIcon={areAllOrganizationsSelected ? <RemoveIcon/> : <AddIcon/>}
                                label={areAllOrganizationsSelected ?
                                    i18n.translate('newDocSign.modal.removeFromAllOrganizations') :
                                    i18n.translate('newDocSign.modal.assignToAllOrganizations')
                                }
                                onDelete={onClickAssignToAll}
                            />
                        </div>
                    )}

                    <ChipsPaginationWrapper
                        currentPage={orgCurrentPage}
                        totalCount={orgTotalCount}
                        pageSize={SIGNDOC_ASSIGNMENT_PAGINATION.USERS_PAGE_SIZE}
                        isLoading={isLoadingOrgs}
                        onPageChange={setOrgPage}
                        selectedCount={selectedOrganizationsCount}
                        selectedLabel={i18n.translate('learningManagement.createEditNewSignDocModal.selectedOrgsLabel')}
                        className="orgChipsPagination"
                        emptyMessage={i18n.translate('learningManagement.createEditNewSignDocModal.noOrganizations')}
                    >
                        {/* UNIFIED VIEW: All orgs shown, selected ones visually marked */}
                        {map(availableOrganizations, (org) => {
                            const organization = get(org, 'organization')
                            const isSelected = selectedOrganizations.has(organization.id)

                            return (
                                <div className="assignmentChip" key={organization.id}>
                                    <Chip
                                        className={isSelected ? 'selectedChip' : ''}
                                        deleteIcon={isSelected ? <RemoveIcon/> : <AddIcon/>}
                                        label={organization.orgName}
                                        onDelete={onClickOrganization({organizationId: organization.id, isSelected})}
                                    />
                                </div>
                            );
                        })}
                    </ChipsPaginationWrapper>
                </div>
            )
        };

        // Client-side search filter + pagination for the selected users sub-tab
        const filteredSelectedUsers = selectedUsersSearchQuery.trim()
            ? selectedUsersDetails.filter(user => {
                const query = selectedUsersSearchQuery.trim().toLowerCase();
                const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
                return fullName.includes(query) || (user.phone && user.phone.includes(query));
            })
            : selectedUsersDetails;

        const selectedUsersPageData = filteredSelectedUsers.slice(
            selectedUsersPage * SIGNDOC_ASSIGNMENT_PAGINATION.USERS_PAGE_SIZE,
            (selectedUsersPage + 1) * SIGNDOC_ASSIGNMENT_PAGINATION.USERS_PAGE_SIZE
        );

        const allUsersSubTab = {
            value: 'allUsers',
            label: i18n.translate('newDocSign.modal.assignmentSubTabs.allUsers'),
            content: (
                <div className="allUsersSubTabContent">
                    <div className="individualUsersSearchRow">
                        <FormInput
                            dontShowErrorMessage
                            id="userSearch"
                            label={i18n.translate('learningManagement.createEditNewSignDocModal.individualUsersSection.searchLabel')}
                            className="userSearchInput"
                            value={userSearchQuery}
                            onChange={onUserSearchChange}
                            placeholder={i18n.translate('learningManagement.createEditNewSignDocModal.individualUsersSection.searchPlaceholder')}
                        />
                        <UploadButton
                            text={i18n.translate('signDocModal.importUsers.importFromFile')}
                            uniqId="import-users-file-input"
                            accept=".csv,.xlsx,.xls"
                            onInputChange={handleImportFileSelected}
                            icon={<ImportIcon />}
                            color={buttonColors.GREY}
                            className="importUsersButton"
                            data-testid="import-users-upload-button"
                        />
                    </div>

                    <ChipsPaginationWrapper
                        currentPage={userCurrentPage}
                        totalCount={userTotalCount}
                        pageSize={SIGNDOC_ASSIGNMENT_PAGINATION.USERS_PAGE_SIZE}
                        isLoading={isLoadingUsers}
                        onPageChange={setUserCurrentPage}
                        selectedCount={selectedPersonalUsersCount}
                        selectedLabel={i18n.translate('learningManagement.createEditNewSignDocModal.selectedLabel')}
                        className="userChipsPagination"
                        emptyMessage={i18n.translate('learningManagement.createEditNewSignDocModal.noUsers')}
                    >
                        {map(availableUsers, (user) => {
                            const userOrgId = `user_${user.id}`;
                            const isSelected = selectedOrganizations.has(userOrgId);

                            return (
                                <div className="assignmentChip" key={user.id}>
                                    <Chip
                                        className={`${isSelected ? 'selectedChip' : ''} userChipWithPhone`}
                                        deleteIcon={isSelected ? <RemoveIcon/> : <AddIcon/>}
                                        label={
                                            <span className="userChipLabel">
                                                <span className="userChipName">{`${user.firstName} ${user.lastName}`}</span>
                                                {user.phone && (
                                                    <span className="userChipPhone">
                                                        ({formatPhoneForDisplay({phone: user.phone, countryCode: companyContext.countryCode})})
                                                    </span>
                                                )}
                                            </span>
                                        }
                                        onDelete={onToggleUser({user, isSelected})}
                                    />
                                </div>
                            );
                        })}
                    </ChipsPaginationWrapper>
                </div>
            )
        };

        const selectedUsersSubTabDef = {
            value: 'selectedUsers',
            label: `${i18n.translate('newDocSign.modal.assignmentSubTabs.selected')} (${selectedPersonalUsersCount})`,
            content: (
                <div className="selectedUsersSubTabContent">
                    {isLoadingSelectedUsers ? (
                        <LoadingSpinner />
                    ) : selectedUsersDetails.length === 0 ? (
                        <div className="selectedUsersEmptyState">
                            {i18n.translate('newDocSign.modal.assignmentSubTabs.noSelectedUsers')}
                        </div>
                    ) : (
                        <>
                            <FormInput
                                dontShowErrorMessage
                                id="selectedUserSearch"
                                label={i18n.translate('learningManagement.createEditNewSignDocModal.individualUsersSection.searchLabel')}
                                className="userSearchInput"
                                value={selectedUsersSearchQuery}
                                onChange={(e) => {
                                    setSelectedUsersSearchQuery(e.target.value);
                                    setSelectedUsersPage(0);
                                }}
                                placeholder={i18n.translate('learningManagement.createEditNewSignDocModal.individualUsersSection.searchPlaceholder')}
                            />
                            <ChipsPaginationWrapper
                                currentPage={selectedUsersPage}
                                totalCount={filteredSelectedUsers.length}
                                pageSize={SIGNDOC_ASSIGNMENT_PAGINATION.USERS_PAGE_SIZE}
                                isLoading={false}
                                onPageChange={setSelectedUsersPage}
                                className="selectedUserChipsPagination"
                                emptyMessage={i18n.translate('learningManagement.createEditNewSignDocModal.noUsers')}
                            >
                                {map(selectedUsersPageData, (user) => (
                                    <div className="assignmentChip" key={user.id}>
                                        <Chip
                                            className="selectedChip userChipWithPhone"
                                            deleteIcon={<RemoveIcon />}
                                            label={
                                                <span className="userChipLabel">
                                                    <span className="userChipName">{`${user.firstName} ${user.lastName}`}</span>
                                                    {user.phone && (
                                                        <span className="userChipPhone">
                                                            ({formatPhoneForDisplay({phone: user.phone, countryCode: companyContext.countryCode})})
                                                        </span>
                                                    )}
                                                </span>
                                            }
                                            onDelete={() => removeSelectedUser(user.id)}
                                        />
                                    </div>
                                ))}
                            </ChipsPaginationWrapper>
                        </>
                    )}
                </div>
            )
        };

        const individualUsersTab = {
            value: 'individualUsers',
            label: i18n.translate('newDocSign.modal.assignmentTabs.individualUsers'),
            content: (
                <div className="individualUsersSection">
                    <LocalTabs
                        defaultTab="allUsers"
                        variant="fullWidth"
                        scrollButtons={false}
                        tabs={[allUsersSubTab, selectedUsersSubTabDef]}
                        onTabChange={onSelectedUsersSubTabChange}
                    />
                </div>
            )
        };

        return [organizationsTab, individualUsersTab];
    }, [
        selectedCategory,
        selectedSignDocType,
        availableOrganizations,
        selectedOrganizations,
        areAllOrganizationsSelected,
        selectedOrganizationsCount,
        orgCurrentPage,
        orgTotalCount,
        isLoadingOrgs,
        orgSearchQuery,
        availableUsers,
        selectedPersonalUsersCount,
        userSearchQuery,
        userCurrentPage,
        userTotalCount,
        isLoadingUsers,
        selectedUsersDetails,
        selectedUsersPage,
        selectedUsersSearchQuery,
        isLoadingSelectedUsers,
        i18n,
        requiresOrganizationAssignment,
        hasSelectedOrganizations,
        onClickAssignToAll,
        onClickOrganization,
        onUserSearchChange,
        onOrgSearchChange,
        handleImportFileSelected,
        onSelectedUsersSubTabChange,
        removeSelectedUser
    ]);

    /**
     * Gets the modal title based on mode (create, edit, or version creation)
     * @returns {string} Modal title text
     */
    const getModalTitle = () => {
        if (isNewVersion) {
            const baseTitle = i18n.translate('newDocSign.modal.newVersionTitle');
            if (signDoc && signDoc.docName) {
                return `${baseTitle} - ${signDoc.docName}`;
            }
            return baseTitle;
        }
        if (editMode) {
            return i18n.translate('newDocSign.modal.editSignDocTitle');
        }
        return i18n.translate('newDocSign.modal.title');
    };

    return <>
    <Modal
        open={newDocSignModalOpen}
        onClose={closeNewDocSignModal}>
        <div className="CreateEditNewDocSignModal modalWrapper">
            <div className="modalTitle">
                <div>{getModalTitle()}</div>
            </div>
            <div className="modalContent">
                {isLoading ? <LoadingSpinner/> : ''}
                <div className="inputsContainer">
                    <div className="typeWrapper row">
                        <div className={companyContext.featureFlags[FeatureFlags.KNOWLEDGE_CENTER] ? `col-50 margin-left` : `col-100`}>
                            <SelectBox options={signDocOptions} value={selectedSignDocType}
                                       disabled={(signDocOptions && signDocOptions.length <= 1) || editMode || isNewVersion}
                                       label={String(i18n.translate('newDocSign.modal.selectSignDocType'))}
                                       onChange={onSelectedSignDocTypeOption}/>
                        </div>

                        {(companyContext.featureFlags[FeatureFlags.KNOWLEDGE_CENTER] || companyContext.featureFlags[FeatureFlags.CERTIFICATION]) ? (
                            <div className="col-50 margin-right">
                                <Tooltip
                                    title={getCategoryDisabledTooltip() || ''}
                                    placement="top"
                                    arrow
                                    disableHoverListener={!getCategoryDisabledTooltip()}
                                >
                                    <span>
                                        <SelectBox
                                            options={getCategoryOptions(companyContext, allowedCategories, i18n)}
                                            value={selectedCategory}
                                            disabled={isCategoryDisabled()}
                                            label={String(i18n.translate('certifications.category'))}
                                            onChange={onSelectCategory}
                                        />
                                    </span>
                                </Tooltip>
                            </div>
                        ) : ''}

                    </div>
                    {/* Status SelectBox - Full width */}
                    <div className="statusWrapper">
                        <SelectBox
                            options={[
                                { value: SIGNDOC_STATUS.ACTIVE, text: i18n.translate('newDocSign.modal.status.ACTIVE') },
                                { value: SIGNDOC_STATUS.ARCHIVED, text: i18n.translate('newDocSign.modal.status.ARCHIVED') }
                            ]}
                            value={signDocStatus}
                            label={i18n.translate('newDocSign.modal.status.label')}
                            onChange={(e) => setSignDocStatus(e.target.value)}
                            dataTestId="status-select"
                        />
                    </div>
                    {/* Frequency Section - GroupType and Recurring Duration */}
                    <div className="frequencySectionWrapper">
                        <div className="groupTypeWrapper">
                            <SelectBox
                                options={[
                                    { value: SIGNDOC_GROUP_TYPES.PARALLEL, text: i18n.translate('newDocSign.modal.groupType.PARALLEL') },
                                    { value: SIGNDOC_GROUP_TYPES.SEQUENTIAL, text: i18n.translate('newDocSign.modal.groupType.SEQUENTIAL') }
                                ]}
                                value={groupType}
                                label={i18n.translate('newDocSign.modal.groupType.label')}
                                onChange={(e) => setGroupType(e.target.value)}
                                disabled={isGroupTypeChangeDisabled}
                                dataTestId="group-type-select"
                            />
                        </div>
                        {/* Recurring Duration - Only shown when groupType is SEQUENTIAL */}
                        {groupType === SIGNDOC_GROUP_TYPES.SEQUENTIAL && (
                            <div className="recurringWrapper">
                                <DurationPicker
                                    value={recurringDuration}
                                    onChange={onRecurringDurationChange}
                                    allowedUnits={RECURRING_DURATION_UNITS}
                                    dataTestId="recurring-duration-picker"
                                />
                            </div>
                        )}
                        {/* Next Version Date - Required when transitioning PARALLEL→SEQUENTIAL */}
                        {isTransitioningToSequential && (
                            <div className="CreateEditNewSignDocModal__recurring-at-section">
                                <Alert severity="info" className="CreateEditNewSignDocModal__recurring-at-disclaimer">
                                    {i18n.translate('newDocSign.modal.recurring.transitionDisclaimer')}
                                </Alert>
                                <div className="CreateEditNewSignDocModal__recurring-at-picker">
                                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
                                        <DatePicker
                                            value={recurringAt}
                                            onChange={setRecurringAt}
                                            open={isRecurringAtCalendarOpen}
                                            onOpen={() => setIsRecurringAtCalendarOpen(true)}
                                            onClose={() => setIsRecurringAtCalendarOpen(false)}
                                            disablePast={!RECURRING_SIGNDOC_ALLOW_PAST_DATE}
                                            label={i18n.translate('newDocSign.modal.recurring.nextVersionDateLabel')}
                                            data-testid="recurring-at-date-picker"
                                            slotProps={{
                                                field: { readOnly: true },
                                                textField: { onClick: () => setIsRecurringAtCalendarOpen(true) }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </div>
                        )}
                    </div>
                    <FormInput
                        required
                        dontShowErrorMessage
                        id="docName"
                        label={getDocNameLabel({ type: selectedSignDocType })}
                        className="newDocSignInput"
                        value={docName}
                        onChange={onDocNameChange}
                    />

                    <FormInput
                        dontShowErrorMessage
                        id="name"
                        label={getDocDescriptionLabel({ type: selectedSignDocType })}
                        className="newDocSignInput"
                        value={docDescription}
                        onChange={onDocDescriptionChange}
                    />

                    {/* Course-specific fields for FRONTAL_COURSE and ONLINE_COURSE */}
                    {isCourseType() ? (
                        <div className="courseFieldsContainer">
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
                                <DateTimePicker
                                    label={i18n.translate('courseFields.sessionStartDate')}
                                    value={sessionStartDate}
                                    onChange={(newValue) => setSessionStartDate(newValue)}
                                    className="newDocSignInput"
                                    slotProps={{
                                        textField: {
                                            className: "newDocSignInput"
                                        }
                                    }}
                                />
                            </LocalizationProvider>

                            {/* Location field - shows different label based on course type */}
                            <FormInput
                                required
                                error={!!courseLocationError}
                                dontShowErrorMessage
                                id="courseLocation"
                                label={i18n.translate(
                                    isFrontalCourse()
                                        ? 'courseFields.location'
                                        : 'courseFields.meetingLink'
                                )}
                                className="newDocSignInput"
                                value={courseLocation}
                                onChange={(e) => {
                                    const newLocation = e.target.value;
                                    setCourseLocation(newLocation);
                                    // Validate meeting link for online courses
                                    if (isOnlineCourse() && newLocation && newLocation.trim()) {
                                        const validation = validateMeetingLink(newLocation);
                                        if (!validation.isValid) {
                                            setCourseLocationError(i18n.translate('courseFields.invalidMeetingLink'));
                                        } else {
                                            setCourseLocationError(null);
                                        }
                                    } else {
                                        setCourseLocationError(null);
                                    }
                                }}
                            />
                            {courseLocationError && courseLocation ? (
                                <div className="linkUrlError">{courseLocationError}</div>
                            ) : null}

                        </div>
                    ) : ''}

                    {getRequiresLink({ type: selectedSignDocType }) ? <FormInput
                        error={!!linkUrlError}
                        dontShowErrorMessage
                        required
                        id="linkUrl"
                        label={i18n.translate('newDocSign.modal.linkUrl')}
                        className="newDocSignInput"
                        value={linkUrl}
                        onChange={onLinkUrlChange}/> : ''}
                    {linkUrlError && linkUrl ? <div className="linkUrlError">{linkUrlError}</div> : ''}
                </div>
                {getRequiresFileUpload({ type: selectedSignDocType }) && !editMode ? <div className="uploadFileContainer">
                    <FileUploadBox
                        uploadFiles={onSelectedDocFiles}
                        maxSize={fileUploadConfig.maxSize}
                        accept={fileUploadConfig.accept}
                        supportedFileTypesForNotifications={fileUploadConfig.supportedFileTypesForNotifications}>
                        <div>{files && files.length > 0 ? get(files, '[0].path') : ''}</div>
                        <div>
                            {(progress && progress !== 100) ? String(i18n.translate('uploadBox.uploading')) + progress + '%' : ''}
                        </div>
                        <div>
                            {(progress && progress === 100) ? String(i18n.translate('uploadBox.uploaded')) : ''}
                        </div>
                        <div>
                            <div>{(!files || files.length <= 0) ? getDocDropLabel({ type: selectedSignDocType }) : ''}</div>
                        </div>
                    </FileUploadBox>
                </div> : ''}
                {getShouldShowAllowFastForwardCheckbox({ type: selectedSignDocType }) ?
                        <div className="allowFastForwardWrapper">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={allowFastForward}
                                        onChange={onAllowFastForwardChange}
                                    />
                                }
                                label={i18n.translate(`newDocSign.modal.allowFastForward.${selectedSignDocType}`)}
                            />
                        </div> : ''
                    }
                { questionnaireTypes[selectedSignDocType] || (editMode && questionnaireTypes[get(signDoc, 'type')]) ? <div className="formsWrapper">
                    {selectedSignDocType === SignDocTypes.EXAM ? (
                        <FormInput
                            className={'questionnaireInput'}
                            type={'number'}
                            dontShowErrorMessage
                            id="passGrade"
                            label={i18n.translate('questionnaire.modal.passGrade')}
                            value={examPassGrade}
                            onChange={onExamPassGradeChange}
                        />
                    ) : null}

                    {/* Show survey settings only for survey types */}
                    {selectedSignDocType === SignDocTypes.SURVEY ? (
                        <div className="surveySettingsWrapper">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isAnonymous}
                                        onChange={(e) => setIsAnonymous(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <div>
                                        <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                                            {i18n.translate('questionnaireQuestions.survey.anonymousToggle')}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
                                            {i18n.translate('questionnaireQuestions.survey.anonymousDescription')}
                                        </div>
                                    </div>
                                }
                            />
                        </div>
                    ) : null}
                </div> : ''}

                {signDocSupportsFinishByDuration && (
                    <div className="finishByDurationWrapper">
                        <div className="finishByDurationToggleWrapper">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showFinishByDurationPicker}
                                        onChange={(e) => setShowFinishByDurationPicker(e.target.checked)}
                                        data-testid="finish-by-duration-toggle"
                                    />
                                }
                                label={
                                    <span className="finishByDurationLabel">
                                        {i18n.translate('signDoc.finishByDuration.toggleLabel')}
                                        <Tooltip title={i18n.translate('signDoc.finishByDuration.note')} placement="top" arrow>
                                            <InfoOutlinedIcon fontSize="small" className="finishByDurationInfoIcon" />
                                        </Tooltip>
                                    </span>
                                }
                            />
                        </div>

                        {showFinishByDurationPicker && (
                            <div className="finishByDurationSection">
                                <DurationPicker
                                    value={{value: finishByDuration, unit: DurationUnits.DAYS}}
                                    onChange={(newDuration) => onFinishByDurationChange({newDuration})}
                                    label={i18n.translate('signDoc.finishByDuration.title')}
                                    allowedUnits={[DurationUnits.DAYS]}
                                    dataTestId="finish-by-duration-picker"
                                />
                                <div className="finishByDurationNote">
                                    {i18n.translate('signDoc.finishByDuration.note')}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Free-path mode toggle for LEARNING_PATH SignDocs — placed right after
                    "Time to complete" so the LP-specific options live together.
                    Persists `additionalData.unorderedLearningPathTasks` via the same payload
                    as everything else in this modal — no separate API call. Confirmation
                    dialog only shows when turning OFF on an already-assigned LP. */}
                {selectedSignDocType === SignDocTypes.LEARNING_PATH ? (
                    <div className="unorderedLearningPathTasksWrapper">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={unorderedLearningPathTasks}
                                    onChange={(e) => handleFreePathToggle(e.target.checked)}
                                />
                            }
                            label={i18n.translate('learningPath.freePath.checkboxLabel')}
                        />
                        <div className="unorderedLearningPathTasksNote">
                            {i18n.translate('learningPath.freePath.checkboxHelper')}
                        </div>
                    </div>
                ) : null}

                {/* MustDo toggle - marks the SignDoc as mandatory for the external compliance API */}
                {hasMustDoComplianceFF ? (
                    <div className="isMustDoWrapper">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isMustDo}
                                    onChange={(e) => setIsMustDo(e.target.checked)}
                                    data-testid="is-must-do-toggle"
                                />
                            }
                            label={
                                <span className="isMustDoLabel">
                                    {i18n.translate('newDocSign.modal.isMustDo.label')}
                                    <Tooltip title={i18n.translate('newDocSign.modal.isMustDo.tooltip')} placement="top" arrow>
                                        <InfoOutlinedIcon fontSize="small" className="isMustDoInfoIcon" />
                                    </Tooltip>
                                </span>
                            }
                        />
                    </div>
                ) : null}

                {/* Assignment Tabs - Organizations vs Individual Users */}
                {toShowOrganizationChips ? (
                    <div className="assignmentSection">
                        <LocalTabs
                            defaultTab="organizations"
                            variant="fullWidth"
                            scrollButtons={false}
                            tabs={assignmentTabs}
                        />
                    </div>
                ) : ''}
                {SignDocTypesThatSupportApprovals.includes(selectedSignDocType) ? (
                    <div className="enableApprovalsWrapper">
                        <Tooltip
                            title={
                                selectedSignDocType === SignDocTypes.UPLOAD_DOCUMENT
                                    ? i18n.translate(`signDocPage.uploadDocument.enableApprovals.${enableApprovals ? 'enabledNote' : 'disabledNote'}`) + ' ' + (enableApprovals ? i18n.translate('learningManagement.createEditNewSignDocModal.approversSection.defaultInfo') : '')
                                    : i18n.translate('signDocPage.courseAttendanceApprovals.description')
                            }
                            placement="right"
                            arrow
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={enableApprovals}
                                        onChange={(e) => setEnableApprovals(e.target.checked)}
                                    />
                                }
                                label={
                                    selectedSignDocType === SignDocTypes.UPLOAD_DOCUMENT
                                        ? i18n.translate('signDocPage.uploadDocument.enableApprovals.toggleLabel')
                                        : i18n.translate('signDocPage.courseAttendanceApprovals.sectionTitle')
                                }
                            />
                        </Tooltip>
                    </div>
                ) : null}

                {showSwitchToOrderedDialog && (
                    <ConfirmationModal
                        confirmationModalOpen={showSwitchToOrderedDialog}
                        onCloseConfirmationModal={() => setShowSwitchToOrderedDialog(false)}
                        title={i18n.translate('learningPath.freePath.confirmSwitchToOrderedTitle')}
                        message={i18n.translate('learningPath.freePath.confirmSwitchToOrderedBody')}
                        confirmText={i18n.translate('learningPath.freePath.confirmSwitchToOrderedConfirm')}
                        onConfirm={() => {
                            setUnorderedLearningPathTasks(false);
                            setShowSwitchToOrderedDialog(false);
                        }}
                        onCancel={() => setShowSwitchToOrderedDialog(false)}
                        destructive
                    />
                )}

                {/* Approver Selection Section - Shows when enableApprovals is true (always true for courses) */}
                {enableApprovals && SignDocTypesThatSupportApprovals.includes(selectedSignDocType) ? (
                    <div className="approversSection">
                        <div className="approverSelectorsWrapper">
                            <div className="approverSelector">
                                <MultiSelectAutocomplete
                                    fetchOptions={({ query, page }) => fetchApproverUsers({ query, page: page || 0 })}
                                    onSelectionChange={(selected) => {
                                        setSelectedApproverUsers(selected);
                                    }}
                                    initialSelectedValues={selectedApproverUsers}
                                    label={
                                        (selectedSignDocType === SignDocTypes.FRONTAL_COURSE ||
                                         selectedSignDocType === SignDocTypes.ONLINE_COURSE)
                                            ? i18n.translate('signDocPage.courseAttendanceApprovals.userLabel')
                                            : i18n.translate('learningManagement.createEditNewSignDocModal.approversSection.userLabel')
                                    }
                                />
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Expiry Duration Section - Only for CERTIFICATION category */}
                {selectedCategory === SignDocCategories.CERTIFICATION && (
                    <div className="expiryDurationWrapper">
                        <div className="expiryToggleWrapper">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showExpiryDuration}
                                        onChange={(e) => setShowExpiryDuration(e.target.checked)}
                                    />
                                }
                                label={i18n.translate('certifications.expiryDuration.toggleLabel')}
                            />
                        </div>

                        {showExpiryDuration && (
                            <div className="expiryDurationSection">
                                <DurationPicker
                                    value={expiryDuration}
                                    onChange={onExpiryDurationChange}
                                    label={i18n.translate('certifications.expiryDuration.title')}
                                    allowedUnits={VALID_DURATION_UNITS}
                                    translationPrefix="certifications.expiryDuration.units"
                                    dataTestId="expiry-duration-picker"
                                />
                                <div className="expiryDurationNote">
                                    {i18n.translate('certifications.expiryDuration.note')}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {toShowOrganizationChips && companyContext.featureFlags[FeatureFlags.NOTIFY_NEW_TASK_TO_USER] &&
                    <div className="notifyUserWrapper">
                        <Tooltip
                            title={i18n.translate('newDocSign.modal.notifyUsersOfNewTaskTooltip')}
                            placement="top"
                            arrow
                        >
                            <FormControlLabel control={<Switch
                                checked={notifyOrganizationUserOnNewTask}
                                onChange={onSendNewTaskForUserChange}
                            />} label={
                                <span className="notifyUserLabel">
                                    {i18n.translate('newDocSign.modal.notifyUsersOfNewTask')}
                                    <InfoOutlinedIcon fontSize="small" className="notifyUserInfoIcon" />
                                </span>
                            }/>
                        </Tooltip>
                    </div> 
                }
                {SignDocsAssingOrganizationInUpdate[selectedSignDocType] && !editMode &&
                        <div className="organizationAssignmentInfoWrapper">
                            <Alert
                                severity="info"
                                icon={<InfoIcon />}
                                className="organizationAssignmentAlert"
                            >
                                {i18n.translate('newDocSign.modal.organizationAssignmentInfo')}
                            </Alert>
                        </div>
                    }
                {notifyOrganizationUserOnNewTask ? <div className="messagingTypeWrapper">
                    <MessageTypeSelectionButtons
                        onSelectedMessageTypeChange={onSelectedMessagingTypeChange}
                        selectedMessagingTypes={selectedMessagingTypes}
                    />
                </div> : ''}
            </div>
            <div className="modalFooter">
                <Button
                    color={buttonColors.GREY}
                    onClick={closeNewDocSignModal}
                    text={i18n.translate('general.cancel')}
                />
                <Tooltip
                    title={quotaCheckData && !quotaCheckData.canSend ? i18n.translate('newDocSign.modal.quotaWarning.disableNotificationToSave') : ''}
                    placement="top"
                    arrow
                >
                    <span>
                        <Button
                            color={buttonColors.YELLOW}
                            disabled={!canSubmit || (quotaCheckData && !quotaCheckData.canSend)}
                            onClick={handleAssignmentWarningModalToAppear}
                            text={getSignDocButtonText}
                        />
                    </span>
                </Tooltip>
                {extraSaveButtonText ? <Tooltip
                    title={quotaCheckData && !quotaCheckData.canSend ? i18n.translate('newDocSign.modal.quotaWarning.disableNotificationToSave') : ''}
                    placement="top"
                    arrow
                >
                    <span>
                        <Button
                            color={buttonColors.YELLOW}
                            disabled={!canSubmit || (quotaCheckData && !quotaCheckData.canSend)}
                            onClick={async () => {
                                if (requiresOrganizationAssignment() && !hasSelectedOrganizations()) {
                                    setOrganizationAssignmentWarningModal(true)
                                    return
                                }
                                await checkQuotaAndSave({withRoute: true})
                            }}
                            text={extraSaveButtonText}
                        />
                    </span>
                </Tooltip> : ''}
            </div>
        </div>
        <OrganizationAssignmentWarningModal
            open={organizationAssignmentWarningModal}
            onClose={closeOrganizationAssignmentWarningModal}
            onConfirm={handleOrganizationAssignmentWarningConfirm}
        />
        {/* Sequential Active Version Conflict Modal - only render when needed */}
        {showSequentialConflictModal && (
            <ConfirmationModal
                confirmationModalOpen={showSequentialConflictModal}
                onCloseConfirmationModal={handleSequentialConflictDecline}
                title={i18n.translate('signDoc.sequentialActiveVersionModal.title')}
                message={i18n.translate('signDoc.sequentialActiveVersionModal.message', {
                    context: {activeVersionName: get(sequentialConflictInfo, 'activeVersionName', '')}
                })}
                messageBody={i18n.translate('signDoc.sequentialActiveVersionModal.subMessage')}
                onConfirm={handleSequentialConflictAgree}
                onCancel={handleSequentialConflictDecline}
                confirmText={i18n.translate('signDoc.sequentialActiveVersionModal.agreeButton')}
                cancelText={i18n.translate('signDoc.sequentialActiveVersionModal.declineButton')}
            />
        )}

        {/* Generic Error Modal - for all non-conflict errors */}
        {showGenericErrorModal && (
            <ConfirmationModal
                confirmationModalOpen={showGenericErrorModal}
                onCloseConfirmationModal={() => setShowGenericErrorModal(false)}
                title={i18n.translate('errors.serverError')}
                message={genericErrorMessage}
                onConfirm={() => setShowGenericErrorModal(false)}
                confirmText={i18n.translate('confirm')}
                hideCancelButton={true}
            />
        )}

        {showQuotaWarningModal && (
            <ConfirmationModal
                confirmationModalOpen={showQuotaWarningModal}
                onCloseConfirmationModal={() => {
                    setShowQuotaWarningModal(false)
                    setQuotaCheckData(null)
                }}
                title={i18n.translate('newDocSign.modal.quotaWarning.title')}
                message={i18n.translate('newDocSign.modal.quotaWarning.message', {
                    context: {
                        quotaNeeded: quotaCheckData?.quotaNeeded || 0,
                        quotaAvailable: quotaCheckData?.quotaAvailable || 0,
                        totalUsers: quotaCheckData?.totalUsers || 0
                    }
                })}
                messageBody={i18n.translate('newDocSign.modal.quotaWarning.contactSupport')}
                onConfirm={() => {
                    setShowQuotaWarningModal(false)
                }}
                confirmText={i18n.translate('confirm')}
                hideCancelButton={true}
            />
        )}

        {showUserPaginationError && (
            <AlertDialog
                title={i18n.translate('learningManagement.createEditNewSignDocModal.userPaginationError')}
                alertMessage={i18n.translate('learningManagement.createEditNewSignDocModal.userPaginationErrorMessage')}
                type={AlertTypesEnum.ERROR}
                onOk={() => setShowUserPaginationError(false)}
            />
        )}


        {showOrgPaginationError && (
            <AlertDialog
                title={i18n.translate('learningManagement.createEditNewSignDocModal.orgPaginationError')}
                alertMessage={i18n.translate('learningManagement.createEditNewSignDocModal.orgPaginationErrorMessage')}
                type={AlertTypesEnum.ERROR}
                onOk={() => setShowOrgPaginationError(false)}
            />
        )}

        {showUploadError && (
            <AlertDialog
                title={i18n.translate('learningManagement.createEditNewSignDocModal.uploadError')}
                alertMessage={i18n.translate('learningManagement.createEditNewSignDocModal.uploadErrorMessage')}
                type={AlertTypesEnum.ERROR}
                onOk={() => setShowUploadError(false)}
            />
        )}
    </Modal>

    {/* Import Users Preview Modal - opens when file is processed */}
    <ImportUsersPreviewModal
        open={!!importPreviewData}
        onClose={handleImportCancel}
        matchedUsers={importPreviewData?.matchedUsers || []}
        unmatchedRows={importPreviewData?.unmatchedRows || []}
        onConfirm={handleImportUsersConfirm}
        isLoading={isProcessingImport}
    />
    </>;
}